/**
 * CompletionScreen — the v2 signing ceremony's terminal SUCCESS surface (CB-5.10, FR-55 / UX-DR20).
 *
 * Shown the instant a Finish succeeds (and on re-entry once the signer is `signed`). Reassuring,
 * plain, human: "You're all done" + "your signed copy is on its way to {email}". The sealed,
 * tamper-evident PDF + certificate of completion is produced ASYNCHRONOUSLY by the Epic 6 seal worker
 * and emailed once everyone has signed — it does NOT exist at this moment, so "Download your copy"
 * opens a copy of the document the signer just reviewed (the source PDF, via the token-scoped
 * document-URL endpoint). The microcopy is explicit that the final sealed copy arrives by email, so the
 * affordance is honest rather than a dead button or a fabricated sealed artifact.
 *
 * Self-contained full-page main (mirrors the shell's terminal MessageFrame): tenant-branded, mobile-
 * first, ≥44px targets, visible focus rings — no app chrome.
 */

import { useState } from 'react';
import { CheckCircle2, Download, Loader2 } from 'lucide-react';
import { contractService } from '@/services/contractService';

interface CompletionScreenProps {
  companyName: string;
  logo: string | null;
  accent: string;
  /** Contrast-guarded label ink for the accent primary control (computed by the shell). */
  ink: string;
  signerName: string | null;
  signerEmail: string;
  /** Per-signer access_token — the only credential for the token-scoped document-URL fetch. */
  token: string;
}

export default function CompletionScreen({
  companyName,
  logo,
  accent,
  ink,
  signerName,
  signerEmail,
  token,
}: CompletionScreenProps) {
  const [downloading, setDownloading] = useState(false);
  const [downloadNote, setDownloadNote] = useState<string | null>(null);
  const firstName = (signerName || '').trim().split(' ')[0] || 'there';

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadNote(null);
    const result = await contractService.getDocumentUrlV2(token);
    setDownloading(false);
    if (result.success && result.data?.available && result.data.url) {
      window.open(result.data.url, '_blank', 'noopener,noreferrer');
      return;
    }
    // Honest fallback: the source isn't downloadable here — the sealed copy still arrives by email.
    setDownloadNote(
      `We couldn't open a copy right now, but your signed copy will be emailed to ${signerEmail}.`,
    );
  };

  return (
    <main className="flex min-h-screen flex-col items-center bg-slate-50 px-4 py-10 text-slate-900">
      <div className="w-full max-w-[640px]">
        <header className="flex items-center gap-3">
          {logo ? (
            <img src={logo} alt={`${companyName} logo`} className="h-10 w-auto max-w-[160px] object-contain" />
          ) : (
            <span
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-bold"
              style={{ backgroundColor: accent, color: ink }}
            >
              {(companyName.trim()[0] || '•').toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-slate-900">{companyName}</div>
            <div className="truncate text-sm text-slate-500">contract signing</div>
          </div>
        </header>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
            <CheckCircle2 className="h-9 w-9 text-emerald-600" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold leading-tight text-slate-900">You're all done, {firstName}</h1>
          <p className="mt-3 text-base leading-relaxed text-slate-600">
            Your signed copy is on its way to <span className="font-medium text-slate-900">{signerEmail}</span>.
            A tamper-evident PDF with a certificate of completion is included once everyone has signed.
          </p>

          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="mt-6 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-4 text-base font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60 sm:w-auto"
            style={{ backgroundColor: accent, color: ink }}
          >
            {downloading ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="h-5 w-5" aria-hidden="true" />
            )}
            {downloading ? 'Opening…' : 'Download your copy'}
          </button>

          {downloadNote ? (
            <p className="mt-3 text-sm text-slate-500" role="status">
              {downloadNote}
            </p>
          ) : null}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          You can close this page — nothing else is needed from you.
        </p>
      </div>
    </main>
  );
}
